import { getApiClient } from './client';
import type { InboxItem, Cohort, SendMessageRequest, MessageBody } from '../types/messaging';

export interface InboxResponse {
  items: InboxItem[];
  nextCursor: string | null;
  unreadCount: number;
}

export interface ThreadResponse {
  messages: InboxItem[];
}

export interface SendMessageResponse {
  message: {
    id: string;
    createdAt: number;
    senderId: string;
    targetId: string;
    replyToId: string | null;
    body: MessageBody;
  };
}

export async function getInbox(
  tenant: string,
  options: { limit?: number; cursor?: string; unreadOnly?: boolean } = {}
): Promise<InboxResponse> {
  const client = getApiClient(tenant);
  const params = new URLSearchParams();
  if (options.limit) params.set('limit', String(options.limit));
  if (options.cursor) params.set('cursor', options.cursor);
  if (options.unreadOnly) params.set('unreadOnly', 'true');

  const query = params.toString();
  return client.get<InboxResponse>(`/msg/inbox${query ? `?${query}` : ''}`);
}

export async function getThread(tenant: string, rootId: string): Promise<ThreadResponse> {
  const client = getApiClient(tenant);
  return client.get<ThreadResponse>(`/msg/thread/${rootId}`);
}

export async function sendMessage(
  tenant: string,
  request: SendMessageRequest
): Promise<SendMessageResponse> {
  const client = getApiClient(tenant);
  return client.post<SendMessageResponse>('/msg/send', request);
}

export async function markRead(tenant: string, messageIds: string[]): Promise<void> {
  const client = getApiClient(tenant);
  await client.post('/msg/mark-read', { messageIds });
}

export async function markUnread(tenant: string, messageIds: string[]): Promise<void> {
  const client = getApiClient(tenant);
  await client.post('/msg/mark-unread', { messageIds });
}

export async function archiveMessages(tenant: string, messageIds: string[]): Promise<void> {
  const client = getApiClient(tenant);
  await client.post('/msg/archive', { messageIds });
}

export async function unarchiveMessages(tenant: string, messageIds: string[]): Promise<void> {
  const client = getApiClient(tenant);
  await client.post('/msg/unarchive', { messageIds });
}

export async function getUnreadCount(tenant: string): Promise<{ count: number }> {
  const client = getApiClient(tenant);
  return client.get<{ count: number }>('/msg/unread-count');
}

export async function getMyCohorts(tenant: string): Promise<{ cohorts: Cohort[] }> {
  const client = getApiClient(tenant);
  return client.get<{ cohorts: Cohort[] }>('/msg/my-cohorts');
}

export async function getCoaches(tenant: string): Promise<{ coachIds: string[] }> {
  const client = getApiClient(tenant);
  return client.get<{ coachIds: string[] }>('/msg/coaches');
}

export interface ChatMessage {
  id: string;
  createdAt: number;
  senderId: string;
  senderName: string;
  senderAvatar: string | null;
  body: MessageBody;
  replyToId: string | null;
}

export interface ChatResponse {
  target: {
    id: string;
    name: string;
    avatarUrl: string | null;
    kind: string;
  };
  messages: ChatMessage[];
}

export async function getChatMessages(tenant: string, targetId: string): Promise<ChatResponse> {
  const client = getApiClient(tenant);
  return client.get<ChatResponse>(`/msg/chat/${targetId}`);
}
